import funnelDisplayMediumUrl from "./docx-fonts/FunnelDisplay-Medium.ttf?url";
import interMediumUrl from "./docx-fonts/Inter-Medium.ttf?url";
import interRegularUrl from "./docx-fonts/Inter-Regular.ttf?url";

/** Family names as Word sees them (the fonts' own name tables). */
export const DOCX_FONT_BODY = "Inter";
export const DOCX_FONT_MEDIUM = "Inter Medium";
export const DOCX_FONT_DISPLAY = "Funnel Display Medium";
/** Complex-script fallback: Hebrew and Arabic are not in the embedded subsets. */
export const DOCX_FONT_RTL = "Arial";

export interface DocxEmbeddedFont {
  name: string;
  data: Uint8Array;
}

/**
 * Cap on one font or storyboard-image fetch during a Word export. A request
 * that never settles would otherwise leave the download spinner stuck until
 * reload; past this the asset is simply left out.
 */
export const DOCX_FETCH_TIMEOUT_MS = 15_000;

const FONT_SOURCES: ReadonlyArray<readonly [name: string, url: string]> = [
  [DOCX_FONT_BODY, interRegularUrl],
  [DOCX_FONT_MEDIUM, interMediumUrl],
  [DOCX_FONT_DISPLAY, funnelDisplayMediumUrl],
];

let cached: Promise<DocxEmbeddedFont[]> | null = null;

/**
 * The three font subsets the Word export embeds (see `docx-fonts/README.md`).
 * Fetched once per session, on the first export. Resolves to an empty list if
 * any fetch fails or times out, so the export still downloads — Word then
 * substitutes — and the next export retries.
 */
export function loadDocxFonts(): Promise<DocxEmbeddedFont[]> {
  cached ??= Promise.all(
    FONT_SOURCES.map(async ([name, url]) => {
      const response = await fetch(url, { signal: AbortSignal.timeout(DOCX_FETCH_TIMEOUT_MS) });
      if (!response.ok) throw new Error(`font ${name}: HTTP ${response.status}`);
      return { name, data: new Uint8Array(await response.arrayBuffer()) };
    }),
  ).catch(() => {
    cached = null;
    return [];
  });
  return cached;
}
